// src/services/firebaseService.js
// VERSIÓN MEJORADA: Actualiza automáticamente estado de actividades

import { db, auth } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';

console.log('✅ firebaseService.js cargado');

// ============================================
// AUTENTICACIÓN
// ============================================

export const servicioAuth = {
  registrar: (email, password) => {
    console.log('🔵 Registrando:', email);
    return createUserWithEmailAndPassword(auth, email, password);
  },

  login: (email, password) => {
    console.log('🔵 Login:', email);
    return signInWithEmailAndPassword(auth, email, password);
  },

  logout: () => {
    console.log('🔵 Logout');
    return signOut(auth);
  },

  observarCambiosAuth: (callback) => {
    console.log('🔵 Observando cambios de autenticación');
    return onAuthStateChanged(auth, (usuario) => {
      console.log('👤 Usuario:', usuario?.email || 'No autenticado');
      callback(usuario);
    });
  },
};

// ============================================
// FUNCIÓN AUTOMÁTICA: Actualizar estado
// ============================================

const actualizarEstadoAutomatico = async (actividades) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  for (const actividad of actividades) {
    // Solo procesa actividades que están "Programadas"
    if (actividad.estado !== 'programada') continue;

    try {
      const fechaInicio = new Date(actividad.fechaInicio);
      fechaInicio.setHours(0, 0, 0, 0);

      // Si hoy > fechaInicio y sigue siendo "Programada" → cambiar a "Sin iniciar"
      if (hoy > fechaInicio) {
        console.log(`📌 Actualizando automáticamente: "${actividad.nombre}" de Programada → Sin iniciar`);
        
        const actividadRef = doc(db, 'actividades', actividad.id);
        await updateDoc(actividadRef, {
          estado: 'sin iniciar',
          editadoEn: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('❌ Error actualizando estado automático:', error);
    }
  }
};

// ============================================
// ACTIVIDADES - FIRESTORE
// ============================================

export const servicioActividades = {
  // Crear actividad
  crear: async (actividad, usuarioId) => {
    try {
      console.log('🔵 [CREAR] Intentando crear:', actividad.nombre);
      
      const docRef = await addDoc(collection(db, 'actividades'), {
        ...actividad,
        usuarioId,
        creadoEn: serverTimestamp(),
        editadoEn: serverTimestamp(),
      });
      
      console.log('✅ [CREAR] Actividad creada con ID:', docRef.id);
      return { id: docRef.id, ...actividad };
    } catch (error) {
      console.error('❌ [CREAR] Error:', error);
      throw new Error('No se pudo crear la actividad: ' + error.message);
    }
  },

  // Escuchar actividades - CON ACTUALIZACIÓN AUTOMÁTICA
  escucharActividades: (usuarioId, callback) => {
    try {
      console.log('🔵 [ESCUCHAR] Iniciando escucha para usuario:', usuarioId);
      
      const actividadesRef = collection(db, 'actividades');

      const unsubscribe = onSnapshot(
        actividadesRef,
        async (snapshot) => {
          console.log('📊 [SNAPSHOT] Recibido snapshot');
          console.log('📊 [SNAPSHOT] Número de documentos:', snapshot.size);
          
          const actividades = [];
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            actividades.push({
              id: doc.id,
              ...data,
              creadoEn: data.creadoEn?.toDate?.()?.toISOString() || new Date().toISOString(),
              editadoEn: data.editadoEn?.toDate?.()?.toISOString() || new Date().toISOString(),
            });
          });
          
          console.log('✅ [PROCESADAS] Total de actividades:', actividades.length);
          
          // 🔄 ACTUALIZACIÓN AUTOMÁTICA DE ESTADO
          console.log('🔄 [AUTOMÁTICA] Verificando actividades "Programadas" que pasaron su fecha...');
          await actualizarEstadoAutomatico(actividades);
          
          // Ordenar
          actividades.sort((a, b) => {
            const dateA = new Date(b.creadoEn);
            const dateB = new Date(a.creadoEn);
            return dateA - dateB;
          });
          
          console.log('✅ [ORDENADAS] Actividades ordenadas:', actividades.length);
          
          // Callback
          console.log('🔄 [CALLBACK] Llamando callback con', actividades.length, 'actividades');
          callback(actividades);
        },
        (error) => {
          console.error('❌ [ERROR] Error en onSnapshot:', error);
          callback([]);
        }
      );

      console.log('✅ [UNSUBSCRIBE] Listener configurado');
      return unsubscribe;
    } catch (error) {
      console.error('❌ [EXCEPCIÓN] Error en escucharActividades:', error);
      callback([]);
      return () => console.log('🔴 Unsubscribe (vacío por error)');
    }
  },

  // Actualizar actividad
  actualizar: async (actividadId, datos) => {
    try {
      console.log('🔵 [ACTUALIZAR] ID:', actividadId);
      console.log('🔵 [ACTUALIZAR] Datos:', datos);
      
      const actividadRef = doc(db, 'actividades', actividadId);
      await updateDoc(actividadRef, {
        ...datos,
        editadoEn: serverTimestamp(),
      });
      
      console.log('✅ [ACTUALIZAR] Éxito');
    } catch (error) {
      console.error('❌ [ACTUALIZAR] Error:', error);
      throw new Error('No se pudo actualizar: ' + error.message);
    }
  },

  // Eliminar actividad
  eliminar: async (actividadId) => {
    try {
      console.log('🔵 [ELIMINAR] ID:', actividadId);
      await deleteDoc(doc(db, 'actividades', actividadId));
      console.log('✅ [ELIMINAR] Éxito');
    } catch (error) {
      console.error('❌ [ELIMINAR] Error:', error);
      throw new Error('No se pudo eliminar: ' + error.message);
    }
  },

  // Agregar subtarea
  agregarSubtarea: async (actividadId, subtarea) => {
    try {
      console.log('🔵 [SUBTAREA] Agregando a:', actividadId);
      
      const actividadRef = doc(db, 'actividades', actividadId);
      const docSnap = await getDoc(actividadRef);
      
      if (!docSnap.exists()) {
        throw new Error('Actividad no encontrada');
      }

      const actividad = docSnap.data();
      const nuevaSubtarea = {
        id: Date.now(),
        texto: subtarea,
        completada: false,
      };

      const subtareas = actividad.subtareas || [];

      await updateDoc(actividadRef, {
        subtareas: [...subtareas, nuevaSubtarea],
        editadoEn: serverTimestamp(),
      });
      
      console.log('✅ [SUBTAREA] Agregada');
    } catch (error) {
      console.error('❌ [SUBTAREA] Error:', error);
      throw error;
    }
  },

  // Agregar comentario
  agregarComentario: async (actividadId, comentario, usuario) => {
    try {
      console.log('🔵 [COMENTARIO] Agregando a:', actividadId);
      
      const actividadRef = doc(db, 'actividades', actividadId);
      const docSnap = await getDoc(actividadRef);
      
      if (!docSnap.exists()) {
        throw new Error('Actividad no encontrada');
      }

      const actividad = docSnap.data();
      const nuevoComentario = {
        id: Date.now(),
        texto: comentario,
        autor: usuario,
        fecha: new Date().toISOString(),
      };

      const comentarios = actividad.comentarios || [];

      await updateDoc(actividadRef, {
        comentarios: [...comentarios, nuevoComentario],
        editadoEn: serverTimestamp(),
      });
      
      console.log('✅ [COMENTARIO] Agregado');
    } catch (error) {
      console.error('❌ [COMENTARIO] Error:', error);
      throw error;
    }
  },
};

export default {
  servicioAuth,
  servicioActividades,
};
